import React from 'react';
import {
  Modal,
  Typography,
  Descriptions,
  Divider,
  Tag,
  Statistic,
  Row,
  Col,
  Button,
} from 'antd';
import {
  CheckCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import './index.scss';
import { CalculatedOpResultProps } from 'interfaces/pages/organization';
import { useLanguage } from 'hooks/Language/useLanguage';

const { Text } = Typography;

const CalculatedOpResultsModal: React.FC<CalculatedOpResultProps> = ({
  visible,
  onClose,
  calculatedOp,
}) => {
  const { translate } = useLanguage();
  const { data } = calculatedOp;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          variant="filled"
          color="danger"
          key="close"
          onClick={onClose}
        >
          {translate('Close')}
        </Button>,
      ]}
      width={900}
      style={{ top: 20 }}
      className="transaction-modal"
      title={data.OperationName}
    >
      <div className="transaction-result">
        <div className="transaction-header">
          <div className="status-badge">
            <CheckCircleOutlined />
            <span>{translate('Operation Complete')}</span>
          </div>
        </div>

        <Row gutter={24} className="transaction-summary">
          <Col span={12}>
            <Statistic
              title={translate('Total Amount')}
              value={data.TotalAmount}
              prefix={<DollarOutlined />}
              suffix={data.Currency}
              className="amount-statistic"
            />
          </Col>
          <Col span={12}>
            <Statistic
              title={translate('Date')}
              value={formatDate(data.Date)}
              prefix={<CalendarOutlined />}
              className="date-statistic"
            />
          </Col>
        </Row>

        <Divider />

        <Descriptions
          title={translate('Organization Details')}
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          className="details-section"
        >
          <Descriptions.Item label={translate('Organization')}>
            {data.OrganizationName}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Organization ID')}>
            {data.OrganizationID}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Contact PID')}>
            {data.ContactPID}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Description')}>
            {data.Description}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        <Descriptions
          title={translate('Wallet Information')}
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          className="details-section"
        >
          <Descriptions.Item label={translate('Wallet Number')}>
            {data.WalletNumber}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Wallet Balance')}>
            {data.WalletBalance} {data.Currency}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Currency')}>
            <div className="currency-item">
              <img
                src={data.CurrencyFlag}
                alt={data.Currency}
                className="currency-flag"
              />
              <span>{data.Currency}</span>
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={translate('Sufficient Balance')}>
            <Tag
              color={
                data.WalletHasEnoughBalance === 'YES'
                  ? 'green'
                  : 'red'
              }
            >
              {data.WalletHasEnoughBalance}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        <Descriptions
          title={translate('Transaction Details')}
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          className="details-section"
        >
          <Descriptions.Item label={translate('Original Amount')}>
            {data.OriginalAmount} {data.Currency}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Amount Paid')}>
            {data.AmountPaid} {data.Currency}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Service Fees')}>
            {data.ServiceFees} {data.Currency}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Exchange Fees')}>
            {data.ExchangeFees} {data.Currency}
          </Descriptions.Item>
          <Descriptions.Item label={translate('External Fees')}>
            {data.ExternalFees} {data.Currency}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Taxes')}>
            {data.Taxes} {data.Currency}
          </Descriptions.Item>
          <Descriptions.Item
            label={translate('Total Amount')}
            span={2}
          >
            <Text strong className="total-amount">
              {data.TotalAmount} {data.Currency}
            </Text>
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        <Descriptions
          title={translate('Operation Statistics')}
          bordered
          column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
          className="details-section"
        >
          <Descriptions.Item label={translate('Creation Date')}>
            {formatDate(data.CreationDate)}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Execution Date')}>
            {formatDate(data.ExecutionDate)}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Total Recipients')}>
            {data.TotalRecipient}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Payment Count')}>
            {data.PaymentCount}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Failure Count')}>
            {data.FailureCount}
          </Descriptions.Item>
          <Descriptions.Item label={translate('Run Count')}>
            {data.RunCount}
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Modal>
  );
};

export default CalculatedOpResultsModal;
